import { useState } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AnimatedBackground from "@/components/AnimatedBackground";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import { getPlanityUrl } from "@/config/standort";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { MapPin, Sparkles, Heart, Star, ChevronRight, X } from "lucide-react";
import { useLocation } from "react-router-dom";

// Studio images
import studioEmpfang from "@/assets/beautystudio-empfang-22-studios-nuernberg.webp";
import studioWaschplatz from "@/assets/friseursalon-waschplatz-22-studios-nuernberg.webp";
import studioHaarstyling from "@/assets/haarstyling-bereich-22-studios-nuernberg.webp";
import studioLaser from "@/assets/laserbehandlung-raum-22-studios-nuernberg.webp";
import studioNaegel from "@/assets/manikuere-pedikuere-raum-22-studios-nuernberg.webp";
import studioGesicht from "@/assets/gesichtsbehandlung-raum-22-studios-nuernberg.webp";
import heleneImage from "@/assets/team-helene-22-studios-nuernberg-v2.webp";
import juelideImage from "@/assets/team-juelide-22-studios-nuernberg-v2.webp";
import marlonImage from "@/assets/team-marlon-22-studios-nuernberg.webp";
import aleynaImage from "@/assets/team-aleyna-22-studios-nuernberg.webp";
import teamPlaceholderImage from "@/assets/team-placeholder-22-studios-nuernberg.webp";


const studioAreas = [
  {
    image: studioEmpfang,
    alt: "Empfangsbereich im Beautystudio 22 Studios Nürnberg",
    title: "Empfang & Lounge",
    description: "Willkommen bei 22 Studios – unser stilvoller Empfangsbereich mit warmem Ambiente empfängt euch zum Wohlfühlen.",
  },
  {
    image: studioHaarstyling,
    alt: "Haarstyling-Bereich mit Friseursesseln bei 22 Studios Nürnberg",
    title: "Haarstyling",
    description: "Moderne Friseurplätze mit bester Ausstattung – hier entstehen Traumfrisuren für Damen und Herren.",
  },
  {
    image: studioWaschplatz,
    alt: "Waschplatz und Pflegebereich im Friseursalon 22 Studios Nürnberg",
    title: "Wasch- & Pflegebereich",
    description: "Entspannung pur an unseren komfortablen Waschplätzen mit professionellen Pflegeprodukten.",
  },
  {
    image: studioGesicht,
    alt: "Gesichtsbehandlung-Raum mit iS Clinical bei 22 Studios Nürnberg",
    title: "Gesichtsbehandlungen",
    description: "High-End Facials mit iS Clinical – in unserem spezialisierten Treatment-Raum für strahlende Haut.",
  },
  {
    image: studioLaser,
    alt: "Laserbehandlung-Raum für dauerhafte Haarentfernung bei 22 Studios Nürnberg",
    title: "Laser & Haarentfernung",
    description: "Modernste Lasertechnologie in stilvollem Ambiente – für sanfte, dauerhafte Haarentfernung.",
  },
  {
    image: studioNaegel,
    alt: "Maniküre und Pediküre Raum im Nagelstudio 22 Studios Nürnberg",
    title: "Maniküre & Pediküre",
    description: "Unser Nagelstudio – der perfekte Ort für professionelle Maniküre, Pediküre und Nageldesign.",
  },
];

const teamMembers = [
  {
    name: "Helene",
    role: "Laser & Facials",
    specialty: "Dauerhafte Haarentfernung, Facials & individuelle Hautberatung",
    image: heleneImage,
    alt: "Helene vom Team 22 Studios Nürnberg",
  },
  {
    name: "Jülide",
    role: "Frauenhaare",
    specialty: "Damenhaarschnitte, Styling & moderne Haarlooks",
    image: juelideImage,
    alt: "Jülide vom Team 22 Studios Nürnberg",
  },
  {
    name: "Marlon",
    role: "Barber & Friseur",
    specialty: "Herrenhaarschnitte, Barber-Services & moderne Stylings",
    image: marlonImage,
    alt: "Marlon vom Team 22 Studios Nürnberg",
  },
  {
    name: "Aaliyah",
    role: "Friseurmeisterin",
    specialty: "Friseurmeisterin für Haarschnitte, Styling & persönliche Beratung",
    image: aleynaImage,
    alt: "Aaliyah vom Team 22 Studios Nürnberg",
  },
  {
    name: "Maniküre & Pediküre",
    role: "Nail & Foot Care",
    specialty: "Maniküre, Pediküre & gepflegte Nägel",
    image: teamPlaceholderImage,
    alt: "Mitarbeiterin für Maniküre und Pediküre bei 22 Studios Nürnberg",
  },
];

const TeamPage = () => {
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);
  const [hoveredStudio, setHoveredStudio] = useState<number | null>(null);
  const [hoveredMember, setHoveredMember] = useState<number | null>(null);
  const location = useLocation();
  const planityUrl = getPlanityUrl();
  const city = "Nürnberg";



  const visibleTeamMembers = teamMembers;
  const visibleStudioAreas = studioAreas;

  return (
    <main className="min-h-screen bg-background relative overflow-hidden">
      <SEO
        title={`Team & Studio – Beauty-Expertinnen in ${city} | 22 Studios`}
        description={`Lernt unser Team und unser Studio kennen: 22 Studios ${city} – erfahrene Expertinnen für Haare, Facials, Nägel & Haarentfernung.`}
        canonical="https://22studios.de/nuernberg/team"
      />
      <AnimatedBackground />
      <Navigation />

      {/* ── Hero Section ── */}
      <section className="pt-28 pb-8 md:pt-40 md:pb-12 relative z-10">
        <div className="container px-4 md:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-foreground/5 backdrop-blur-sm border border-foreground/10 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-highlight" />
            <span className="text-xs tracking-widest uppercase text-muted-foreground font-medium">Team & Studio</span>
          </div>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-light text-foreground leading-tight tracking-tight mb-4 whitespace-pre-line">
            Die Menschen hinter <span className="text-highlight italic">22 Studios</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-light">
            Ein Blick hinter die Kulissen – lernt unser Studio und die Expertinnen kennen, die mit Leidenschaft für euch da sind.
          </p>
        </div>
      </section>

      {/* ── Studio Tour Section ── */}
      <section className="py-12 md:py-20 relative z-10">
        <div className="container px-4 md:px-6">
          <div className="flex items-center gap-3 mb-8 md:mb-12">
            <div className="px-4 py-1.5 rounded-full bg-foreground/5 backdrop-blur-sm border border-foreground/10">
              <span className="text-xs tracking-widest uppercase text-muted-foreground font-medium flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" /> Unser Studio
              </span>
            </div>
            <div className="h-px flex-1 bg-foreground/10" />
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {visibleStudioAreas.map((area, i) => {
              const isLarge = i === 0 || i === 3;
              return (
                <div
                  key={i}
                  className={`group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ${
                    isLarge ? "col-span-2 md:col-span-2 aspect-[16/9]" : "col-span-1 aspect-[3/4] md:aspect-square"
                  } ${hoveredStudio !== null && hoveredStudio !== i ? "opacity-60 scale-[0.98]" : ""}`}
                  onMouseEnter={() => setHoveredStudio(i)}
                  onMouseLeave={() => setHoveredStudio(null)}
                  onClick={() => setLightboxImage({ src: area.image, alt: area.alt })}
                >
                  <img
                    src={area.image}
                    alt={area.alt}
                    className="w-full h-full object-cover block transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  {/* Info card */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out">
                    <div className="backdrop-blur-xl bg-white/20 rounded-xl p-3 md:p-4 border border-white/30">
              <h3 className="text-white font-medium text-sm md:text-base mb-1">{area.title}</h3>
                      <p className="text-white/80 text-xs md:text-sm font-light leading-relaxed">{area.description}</p>
                    </div>
                  </div>
                  {/* Corner badge */}
                  <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-[10px] tracking-wider uppercase opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                    Anklicken zum Vergrößern
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Team Section ── */}
      <section className="py-12 md:py-20 relative z-10">
        <div className="container px-4 md:px-6">
          <div className="flex items-center gap-3 mb-8 md:mb-12">
            <div className="px-4 py-1.5 rounded-full bg-foreground/5 backdrop-blur-sm border border-foreground/10">
              <span className="text-xs tracking-widest uppercase text-muted-foreground font-medium flex items-center gap-2">
                <Heart className="w-3.5 h-3.5" /> Unser Team
              </span>
            </div>
            <div className="h-px flex-1 bg-foreground/10" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 md:gap-6 max-w-7xl mx-auto">
            {visibleTeamMembers.map((member, i) => (
              <div
                key={i}
                className={`group relative cursor-pointer transition-all duration-500 ${
                  hoveredMember !== null && hoveredMember !== i ? "opacity-50 scale-95" : ""
                }`}
                onMouseEnter={() => setHoveredMember(i)}
                onMouseLeave={() => setHoveredMember(null)}
              >
                {/* Team portrait */}
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-3 bg-gradient-to-br from-primary/20 via-highlight/10 to-accent/20 border border-foreground/5 shadow-soft-lg">
                  <img
                    src={member.image}
                    alt={member.alt}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  {/* Shimmer effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  {/* Hover info overlay */}
                  <div className="absolute inset-0 bg-foreground/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-400 flex flex-col items-center justify-center p-4 text-center">
                    <Star className="w-5 h-5 text-highlight mb-2 animate-pulse" />
                    <p className="text-background text-xs md:text-sm font-medium mb-1">{member.specialty}</p>
                  </div>
                </div>

                {/* Name & role */}
                <div className="text-center">
                  <h3 className="font-medium text-foreground text-sm md:text-base group-hover:text-highlight transition-colors duration-300">
                    {member.name}
                  </h3>
                  <p className="text-muted-foreground text-xs md:text-sm font-light">{member.role}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 md:mt-16 text-center">
            <div className="inline-block backdrop-blur-xl bg-white/40 border border-white/50 rounded-2xl p-6 md:p-8 shadow-soft-lg">
              <p className="text-muted-foreground mb-4 text-sm md:text-base font-light">
                Wir freuen uns darauf, euch persönlich im Studio zu begrüßen 💛
              </p>
              <Button
                size="lg"
                className="bg-foreground text-background hover:bg-foreground/80 px-8 md:px-10 py-5 md:py-6 text-sm md:text-base font-medium rounded-full hover:scale-105 transition-all duration-300"
                onClick={() => window.open(planityUrl, "_blank")}
              >
                Termin buchen
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Lightbox ── */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in cursor-pointer"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 md:top-8 md:right-8 text-white/80 hover:text-white transition-colors z-10"
            onClick={() => setLightboxImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={lightboxImage.src}
            alt={lightboxImage.alt}
            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
      <ScrollToTopButton />
    </main>
  );
};

export default TeamPage;
